import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiPrinter } from 'react-icons/hi';
import { OrderUI } from '@/types/order';
import { safeString, safeUnit } from '@/utils/safe-formatting';
import { toast } from '@/store/toastStore';

// Extended order type with customer object for printing
interface OrderWithCustomer extends OrderUI {
  customer?: {
    id: string;
    code: string;
    name: string;
    phone?: string;
    email?: string;
    address?: {
      province?: string;
      city?: string;
      district?: string;
      commune?: string;
      street_address?: string;
    };
  };
}

interface OrderPrintButtonProps {
  order: OrderUI;
}

const OrderPrintButton: React.FC<OrderPrintButtonProps> = ({ order }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handlePrint = async () => {
    setLoading(true);
    
    try {
      // Filter only normal order lines (exclude rework lines)
      const normalLines = (order.orderLines || []).filter(line => line.type !== 'rework');
      
      // Generate print content with normal lines only
      const printContent = generatePrintContent(order, normalLines, t);

      // Create a temporary iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow?.document;
      if (!iframeDoc) {
        toast.error(t('orders.print.errors.cannotCreateFile'));
        document.body.removeChild(iframe);
        return;
      }

      iframeDoc.open();
      iframeDoc.write(printContent);
      iframeDoc.close();

      // Wait for content to load before printing
      const PRINT_DELAY_MS = 250;
      const CLEANUP_DELAY_MS = 100;
      
      setTimeout(() => {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
        
        // Remove iframe after printing
        setTimeout(() => {
          if (iframe.parentNode) {
            document.body.removeChild(iframe);
          }
        }, CLEANUP_DELAY_MS);
      }, PRINT_DELAY_MS);

    } catch (error) {
      console.error('Error generating print:', error);
      toast.error(t('orders.print.errors.errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={handlePrint}
        disabled={loading}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        title={t('tooltips.orders.print')}
      >
        <HiPrinter className="h-4 w-4" />
        <span>{loading ? t('orders.print.buttonText.loading') : t('orders.print.buttonText.print')}</span>
      </button>

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p className="text-gray-700 font-medium">{t('orders.print.loading.preparing')}</p>
          </div>
        </div>
      )}
    </>
  );
};

const generatePrintContent = (order: OrderUI, orderLines: any[], t: (key: string) => string): string => {
  const orderWithCustomer = order as OrderWithCustomer;
  
  // Format date for print (DD / MM / YYYY)
  const formatPrintDate = (dateString: string | null | undefined): string => {
    if (!dateString || dateString === '' || dateString === 'N/A') {
      return '--';
    }
    
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return '--';
      }
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day} / ${month} / ${year}`;
    } catch (error) {
      return '--';
    }
  };
  
  // Format address from customer object
  const formatCustomerAddress = (): string => {
    // Try to get address from shippingAddress first
    if (order.shippingAddress) {
      return order.shippingAddress;
    }
    
    // Otherwise, construct from customer.address fields
    if (orderWithCustomer.customer?.address) {
      const addr = orderWithCustomer.customer.address;
      const parts = [
        addr.street_address,
        addr.commune,
        addr.district,
        addr.city,
        addr.province
      ].filter(Boolean);
      return parts.join(', ');
    }
    
    return '';
  };
  
  const orderDate = formatPrintDate(order.createdAt);
  const customerAddress = formatCustomerAddress();
  
  // Calculate total quantity
  const totalQuantity = orderLines.reduce((sum, line) => sum + (line.quantity || 0), 0);

  return `
<!DOCTYPE html>
<html lang="vi">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${t('orders.print.title')}</title>
  <style>
    @media print {
      @page {
        size: A4;
        margin: 10mm 15mm;
      }
      
      body {
        margin: 0;
        padding: 0;
      }
      
      /* Hide browser default headers and footers */
      @page {
        margin-top: 0;
        margin-bottom: 0;
      }
    }
    
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Times New Roman', Times, serif;
      font-size: 16px;
      line-height: 1.4;
      color: #000;
      background: #fff;
      padding: 20px;
    }
    
    .header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 2px solid #000;
    }
    
    .company-info {
      flex: 1;
    }
    
    .company-name {
      font-size: 22px;
      font-weight: bold;
      margin-bottom: 5px;
    }
    
    .company-details {
      font-size: 14px;
      line-height: 1.6;
    }
    
    .bank-info {
      text-align: right;
      font-size: 14px;
      line-height: 1.6;
    }
    
    .title {
      text-align: center;
      margin: 20px 0;
    }
    
    .title h1 {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 5px;
    }
    
    .title .date {
      font-size: 16px;
      font-style: italic;
    }
    
    .order-code {
      text-align: center;
      margin-bottom: 15px;
      font-size: 17px;
      font-weight: bold;
    }
    
    .customer-info {
      margin-bottom: 15px;
      font-size: 16px;
      line-height: 1.6;
    }
    
    .customer-info strong {
      display: inline-block;
      width: 120px;
    }
    
    .customer-name-bold {
      font-weight: bold;
      font-size: 18px;
    }
    
    .order-code-bold {
      font-weight: bold;
      font-size: 18px;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 15px 0;
    }
    
    table th,
    table td {
      border: 1px solid #000;
      padding: 8px 6px;
      text-align: left;
    }
    
    table th {
      background-color: #f0f0f0;
      font-weight: bold;
      text-align: center;
    }
    
    .text-center {
      text-align: center;
    }
    
    .text-right {
      text-align: right;
    }
    
    .total-row {
      font-weight: bold;
    }
    
    .signature {
      margin-top: 40px;
      display: flex;
      justify-content: flex-end;
    }
    
    .signature-box {
      text-align: center;
      min-width: 200px;
    }
    
    .signature-title {
      font-weight: bold;
      margin-bottom: 60px;
    }
    
    .signature-name {
      font-style: italic;
    }
  </style>
</head>
<body>
  <!-- Header -->
  <div class="header">
    <div class="company-info">
      <div class="company-name">PHƯƠNG NAM PROLAB</div>
      <div class="company-details">
        Địa chỉ: Số 89 đường Vạn Đại 2.5, Phường Đình Công,<br>
        Quận Hoàng Mai, TP Hà Nội<br>
        Điện thoại: 0948.156.186 - 0964.839.586
      </div>
    </div>
    <div class="bank-info">
      Chuyển khoản:<br>
      TK: 948156186 - Trần Văn Nhật<br>
      Ngân hàng TMCP Á Châu (ACB) -<br>
      CN Hoàng Cầu
    </div>
  </div>

  <!-- Title -->
  <div class="title">
    <h1>${t('orders.print.title')}</h1>
    <div class="date">${t('orders.print.createdAtLabel')}: ${orderDate}</div>
    <div class="order-code order-code-bold">${t('orders.print.orderCodeLabel')}: ${safeString(order.code).toUpperCase()}</div>
  </div>

  <!-- Customer Info -->
  <div class="customer-info">
    <div><strong>${t('orders.print.customerNameLabel')}:</strong> <span class="customer-name-bold">${safeString(order.customerName).toUpperCase()}</span></div>
    <div><strong>${t('orders.print.phoneLabel')}:</strong> ${safeString(order.customerPhone || '')}</div>
    <div><strong>${t('orders.print.addressLabel')}:</strong> ${safeString(customerAddress)}</div>
    <div><strong>${t('orders.print.sendCarLabel')}:</strong></div>
    <div><strong>${t('orders.print.notesLabel')}:</strong> ${safeString(order.notes || '')}</div>
  </div>

  <!-- Order Lines Table -->
  <table>
    <thead>
      <tr>
        <th style="width: 40px;">${t('orders.print.tableHeaders.stt')}</th>
        <th>${t('orders.print.tableHeaders.content')}</th>
        <th style="width: 60px;">${t('orders.print.tableHeaders.unit')}</th>
        <th style="width: 50px;">${t('orders.print.tableHeaders.quantity')}</th>
        <th style="width: 100px;">${t('orders.print.tableHeaders.notes')}</th>
      </tr>
    </thead>
    <tbody>
      ${orderLines.map((line, index) => `
        <tr>
          <td class="text-center">${index + 1}</td>
          <td>${safeString(line.serviceName)}</td>
          <td class="text-center">${safeUnit(line.unit)}</td>
          <td class="text-center">${line.quantity}</td>
          <td class="text-center">${line.notes ? safeString(line.notes) : ''}</td>
        </tr>
      `).join('')}
      <tr class="total-row">
        <td colspan="3" class="text-right">${t('orders.print.tableHeaders.total')}</td>
        <td class="text-center">${totalQuantity}</td>
        <td></td>
      </tr>
    </tbody>
  </table>

  <!-- Signature -->
  <div class="signature">
    <div class="signature-box">
      <div class="signature-title">${t('orders.print.signature.salesperson')}</div>
      <div class="signature-name">${safeString(order.createdByName || order.createdBy)}</div>
    </div>
  </div>

  <script>
    // Auto-focus for printing
    window.onload = function() {
      window.focus();
    };
  </script>
</body>
</html>
  `.trim();
};

export default OrderPrintButton;
